// ============================================================================
// @torre/expenses — Configuración de Finanzas internas (#1255)
// ============================================================================
// Dos cosas que la compañía ajusta sin tocar código:
//
//   · CATEGORÍAS. A las del sistema (`EXPENSE_CATEGORIES`) se les puede cambiar
//     nombre, color y si sus gastos son fijos o variables; además se crean
//     categorías propias. «Borrar» una categoría con movimientos la ARCHIVA:
//     deja de ofrecerse al capturar, pero la historia la conserva. Las que el
//     sistema usa con un significado propio (nómina, plataformas, equipo,
//     comisiones, impuestos, otros) no se archivan.
//
//   · TIPO DE CAMBIO a la moneda base. Por omisión es el oficial del día; se
//     puede FIJAR un valor desde una fecha (hoy o futura). Nunca es retroactivo:
//     cada fijación vale de su fecha en adelante, y un mes pasado se sigue
//     leyendo con lo que valía entonces.
//
// Lógica pura: el front la usa para pintar y validar; el back (vía
// `build:functions`) para guardar y resolver el tipo de cambio de una fecha.
// ============================================================================

import {
  CATEGORY_ID_RE,
  DEFAULT_FIXED_CATEGORIES,
  EXPENSE_CATEGORIES,
  EXPENSE_CATEGORY_LABELS,
  EXPENSE_COST_TYPES,
  isIsoDate,
  normalizeText,
  type ActiveCategory,
  type BaseExpenseCategory,
  type ExpenseCostType,
  type ValidationResult,
} from "./index";

// --- Categorías -----------------------------------------------------------------

/** Categorías que el sistema usa con significado propio: no se archivan ni eliminan. */
export const PROTECTED_CATEGORIES: readonly BaseExpenseCategory[] = ["nomina", "plataformas", "infraestructura", "hardware", "comisiones", "impuestos", "otros"];

/** Color de cada categoría del sistema (mismo en tablas, gráficas y chips). */
export const BASE_CATEGORY_COLORS: Record<BaseExpenseCategory, string> = {
  plataformas: "#7c3aed",
  infraestructura: "#65a30d",
  nomina: "#059669",
  compras: "#2563eb",
  servicios: "#0891b2",
  impuestos: "#dc2626",
  oficina: "#ca8a04",
  viajes: "#db2777",
  marketing: "#ea580c",
  hardware: "#4f46e5",
  comisiones: "#64748b",
  otros: "#94a3b8",
};

/** Colores que se proponen para las categorías propias, en orden. */
export const CUSTOM_CATEGORY_PALETTE = ["#0d9488", "#be185d", "#b45309", "#1d4ed8", "#15803d", "#9333ea", "#c2410c", "#0e7490", "#a16207", "#e11d48"] as const;

const COLOR_RE = /^#[0-9a-fA-F]{6}$/;
/** Largo máximo del nombre de una categoría. */
export const CATEGORY_NAME_MAX = 60;

/** Lo que se guarda por compañía: los ajustes a las del sistema y las propias. */
export interface StoredCategory {
  id: string;
  label?: { es?: string | null; en?: string | null } | null;
  color?: string | null;
  costType?: ExpenseCostType | null;
  archived?: boolean;
  /** Orden de alta entre las propias (ISO). */
  createdAt?: string | null;
}

/** Una categoría lista para usarse en pantalla. */
export interface CategoryDef extends ActiveCategory {
  label: { es: string; en: string };
  color: string;
  archived: boolean;
  /** Viene con el sistema (no se elimina; quizá sí se archiva). */
  builtin: boolean;
  /** No se puede archivar ni eliminar. */
  protected: boolean;
}

/** Lo que se captura al crear o editar una categoría. */
export interface CategoryInput {
  /** Nombre en español (obligatorio al crear). */
  name?: string | null;
  /** Nombre en inglés; sin él se usa el español. */
  nameEn?: string | null;
  color?: string | null;
  costType?: ExpenseCostType | null;
  archived?: boolean;
}

const isBase = (id: string): id is BaseExpenseCategory => (EXPENSE_CATEGORIES as readonly string[]).includes(id);
const clean = (v: unknown) => String(v ?? "").trim().replace(/\s+/g, " ");

/**
 * Catálogo de la compañía: las del sistema (con lo que la compañía les haya
 * cambiado), en su orden de siempre, y después las propias por orden de alta.
 */
export function categoryCatalog(stored: readonly StoredCategory[] | null | undefined = []): CategoryDef[] {
  const byId = new Map((stored || []).filter((s) => s && typeof s.id === "string").map((s) => [s.id, s]));
  const base: CategoryDef[] = EXPENSE_CATEGORIES.map((id) => {
    const s = byId.get(id);
    const isProtected = PROTECTED_CATEGORIES.includes(id);
    const es = clean(s?.label?.es) || EXPENSE_CATEGORY_LABELS[id].es;
    // Si renombraron el español y no dieron inglés, el inglés sigue al español nuevo.
    const en = clean(s?.label?.en) || (clean(s?.label?.es) ? es : EXPENSE_CATEGORY_LABELS[id].en);
    return {
      id,
      label: { es, en },
      color: COLOR_RE.test(String(s?.color || "")) ? String(s!.color) : BASE_CATEGORY_COLORS[id],
      costType: EXPENSE_COST_TYPES.includes(s?.costType as ExpenseCostType) ? (s!.costType as ExpenseCostType) : DEFAULT_FIXED_CATEGORIES.includes(id) ? "fijo" : "variable",
      archived: !isProtected && s?.archived === true,
      builtin: true,
      protected: isProtected,
    };
  });
  const custom: CategoryDef[] = [...byId.values()]
    .filter((s) => !isBase(s.id) && CATEGORY_ID_RE.test(s.id))
    .sort((a, b) => String(a.createdAt || "").localeCompare(String(b.createdAt || "")) || a.id.localeCompare(b.id))
    .map((s, i) => {
      const es = clean(s.label?.es) || s.id;
      return {
        id: s.id,
        label: { es, en: clean(s.label?.en) || es },
        color: COLOR_RE.test(String(s.color || "")) ? String(s.color) : CUSTOM_CATEGORY_PALETTE[i % CUSTOM_CATEGORY_PALETTE.length],
        costType: EXPENSE_COST_TYPES.includes(s.costType as ExpenseCostType) ? (s.costType as ExpenseCostType) : "variable",
        archived: s.archived === true,
        builtin: false,
        protected: false,
      };
    });
  return [...base, ...custom];
}

/** Id de una categoría nueva a partir de su nombre: «Renta de oficina» → `renta-de-oficina`. */
export function categorySlug(name: string, taken: readonly string[]): string {
  const base =
    normalizeText(name)
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 32) || "categoria";
  let id = base;
  for (let n = 2; taken.includes(id); n += 1) id = `${base}-${n}`;
  return id;
}

export function validateCategoryInput(input: CategoryInput, { creating = false } = {}): ValidationResult {
  const errors: string[] = [];
  const name = clean(input.name);
  if (creating && !name) errors.push("name: requerido");
  if (input.name != null && !creating && !name) errors.push("name: no puede quedar vacío");
  if (name.length > CATEGORY_NAME_MAX) errors.push(`name: máximo ${CATEGORY_NAME_MAX} caracteres`);
  if (clean(input.nameEn).length > CATEGORY_NAME_MAX) errors.push(`nameEn: máximo ${CATEGORY_NAME_MAX} caracteres`);
  if (input.color != null && input.color !== "" && !COLOR_RE.test(String(input.color))) errors.push("color: debe ser #RRGGBB");
  if (input.costType != null && !EXPENSE_COST_TYPES.includes(input.costType)) errors.push("costType: fijo o variable");
  if (input.archived != null && typeof input.archived !== "boolean") errors.push("archived: true o false");
  return { valid: errors.length === 0, errors };
}

export type CategoryChange =
  | { ok: true; stored: StoredCategory[]; category: CategoryDef }
  | { ok: false; status: 404 | 409 | 422; errors: string[] };

/**
 * Crea (`id` nulo) o edita una categoría sobre lo guardado. No permite dos
 * categorías ACTIVAS con el mismo nombre ni archivar una protegida.
 */
export function saveCategoryChange(stored: readonly StoredCategory[] | null | undefined, id: string | null, input: CategoryInput, now: string): CategoryChange {
  const current = categoryCatalog(stored);
  const creating = !id;
  const v = validateCategoryInput(input, { creating });
  if (!v.valid) return { ok: false, status: 422, errors: v.errors };
  const target = creating ? null : current.find((c) => c.id === id);
  if (!creating && !target) return { ok: false, status: 404, errors: ["Categoría no encontrada"] };
  if (target?.protected && input.archived === true) return { ok: false, status: 422, errors: ["Esta categoría la usa el sistema y no se puede archivar"] };

  const name = clean(input.name);
  if (name) {
    const clash = current.find((c) => c.id !== id && !c.archived && normalizeText(c.label.es) === normalizeText(name));
    if (clash) return { ok: false, status: 409, errors: ["Ya hay una categoría con ese nombre"] };
  }

  const list = (stored || []).map((s) => ({ ...s }));
  const newId = creating ? categorySlug(name, current.map((c) => c.id)) : (id as string);
  let entry = list.find((s) => s.id === newId);
  if (!entry) {
    entry = { id: newId };
    if (creating) entry.createdAt = now;
    list.push(entry);
  }
  if (input.name != null || input.nameEn != null) {
    const es = input.name != null ? name : clean(entry.label?.es);
    const en = input.nameEn != null ? clean(input.nameEn) : clean(entry.label?.en);
    entry.label = { es: es || null, en: en || null };
  }
  if (input.color !== undefined) entry.color = input.color ? String(input.color).toLowerCase() : null;
  if (input.costType !== undefined) entry.costType = input.costType ?? null;
  if (input.archived !== undefined) entry.archived = input.archived === true;

  const category = categoryCatalog(list).find((c) => c.id === newId) as CategoryDef;
  return { ok: true, stored: list, category };
}

export type CategoryRemoval =
  | { ok: true; stored: StoredCategory[]; result: "deleted" | "archived" }
  | { ok: false; status: 404 | 422; errors: string[] };

/**
 * «Borrar»: una categoría propia SIN movimientos se elimina; con movimientos, o
 * si es del sistema, se archiva (la historia la sigue mostrando). Las protegidas
 * no se pueden quitar.
 */
export function removeCategory(stored: readonly StoredCategory[] | null | undefined, id: string, inUse: boolean): CategoryRemoval {
  const target = categoryCatalog(stored).find((c) => c.id === id);
  if (!target) return { ok: false, status: 404, errors: ["Categoría no encontrada"] };
  if (target.protected) return { ok: false, status: 422, errors: ["Esta categoría la usa el sistema y no se puede quitar"] };
  const list = (stored || []).map((s) => ({ ...s }));
  if (!target.builtin && !inUse) return { ok: true, stored: list.filter((s) => s.id !== id), result: "deleted" };
  let entry = list.find((s) => s.id === id);
  if (!entry) {
    entry = { id };
    list.push(entry);
  }
  entry.archived = true;
  return { ok: true, stored: list, result: "archived" };
}

// --- Tipo de cambio ---------------------------------------------------------------

/** Monedas que se configuran (a la moneda base, MXN). */
export const FX_CURRENCIES = ["USD", "EUR"] as const;
export type FxCurrency = (typeof FX_CURRENCIES)[number];

/**
 * Una fijación del tipo de cambio: vale de `from` en adelante, hasta la
 * siguiente. `rate: null` = «volver al oficial del día» desde esa fecha.
 */
export interface FxPin {
  /** `YYYY-MM-DD`. */
  from: string;
  rate: number | null;
  /** Quién la capturó y cuándo (ISO). */
  by?: string | null;
  at?: string | null;
}

export type FxPins = Partial<Record<string, FxPin[]>>;

const sortPins = (pins: readonly FxPin[]) => [...pins].sort((a, b) => a.from.localeCompare(b.from));

/**
 * La fijación que aplica en una fecha: la más reciente con `from` ≤ fecha.
 * `null` = no hay valor fijado ese día (aplica el oficial).
 */
export function fxPinAt(pins: readonly FxPin[] | null | undefined, date: string): FxPin | null {
  let found: FxPin | null = null;
  for (const p of sortPins(pins || [])) {
    if (p.from > date) break;
    found = p;
  }
  return found && found.rate != null && found.rate > 0 ? found : null;
}

export function validateFxPin(input: { currency?: unknown; rate?: unknown; from?: unknown }, today: string): ValidationResult {
  const errors: string[] = [];
  if (!(FX_CURRENCIES as readonly string[]).includes(String(input.currency))) errors.push(`currency: ${FX_CURRENCIES.join(" o ")}`);
  if (input.rate !== null) {
    const r = Number(input.rate);
    if (typeof input.rate === "boolean" || !Number.isFinite(r) || r <= 0) errors.push("rate: debe ser mayor que 0 (o null para volver al oficial)");
  }
  if (!isIsoDate(input.from)) errors.push("from: debe ser YYYY-MM-DD");
  // No retroactivo: una fijación con fecha pasada reescribiría meses ya leídos.
  else if (String(input.from) < today) errors.push("from: no puede ser anterior a hoy; el tipo de cambio aplica a entradas futuras");
  return { valid: errors.length === 0, errors };
}

/** Agrega una fijación (o sustituye la de esa misma fecha) y devuelve todo ordenado. */
export function addFxPin(pins: FxPins | null | undefined, currency: string, pin: FxPin): FxPins {
  const out: FxPins = {};
  for (const [c, list] of Object.entries(pins || {})) out[c] = sortPins(list || []);
  const rate = pin.rate == null ? null : Math.round(Number(pin.rate) * 10000) / 10000;
  out[currency] = sortPins([...(out[currency] || []).filter((p) => p.from !== pin.from), { ...pin, rate }]);
  return out;
}
